import { IterationCwIcon, RefreshCwIcon, Settings } from "lucide-react";
import React, { useMemo } from "react";

import LoopConfig from "../LoopBlock/LoopConfig";

import { ForLoopConfiguration, LoopType } from "./types";

import InputVariablesField from "@/components/PowerBlock/Block/InputVariablesField";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import SelectSource, { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toggle } from "@/components/Toggle";
import Checkbox from "@/components/ui/checkbox";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";

interface ConfigurationViewProps {
  loopType: LoopType;
  iteratorSource?: string;
  maxIterations?: number;
  useIteratorSource?: boolean;
  useJsonPath?: boolean;
  inputVariables: string[];
  sources: Record<SourceType, string[]>;
  inputVariablesIsLoading: boolean;
  readonly?: boolean;
  onLoopTypeChange: (type: LoopType) => void;
  onIteratorSourceChange: (source: string | undefined) => void;
  onSourceSelectorChange?: (source: string | undefined) => void;
  onMaxIterationsChange?: (maxIterations: number | undefined) => void;
  onUseIteratorSourceChange?: (useIteratorSource: boolean) => void;
  onUseJsonPathChange?: (useJsonPath: boolean) => void;
  onAutofill: (mappings: Record<string, string>) => void;
  promptConfig?: ForLoopConfiguration["prompt_config"];
  onPromptConfigChange: (config: ForLoopConfiguration["prompt_config"]) => void;
  showVerboseMode?: boolean;
  workflowConfig?: ForLoopConfiguration["workflow_config"];
  onWorkflowConfigChange: (config: ForLoopConfiguration["workflow_config"]) => void;
}

const ConfigurationView = React.memo(
  ({
    loopType,
    iteratorSource,
    maxIterations,
    useIteratorSource = false,
    useJsonPath = false,
    inputVariables,
    sources,
    inputVariablesIsLoading,
    readonly = false,
    onLoopTypeChange,
    onIteratorSourceChange,
    onSourceSelectorChange,
    onMaxIterationsChange,
    onUseIteratorSourceChange,
    onUseJsonPathChange,
    onAutofill,
    promptConfig,
    onPromptConfigChange,
    showVerboseMode = true,
    workflowConfig,
    onWorkflowConfigChange,
  }: ConfigurationViewProps) => {
    const isReadonly = useIsReadonly();
    const LoopIcon = useMemo(() => RefreshCwIcon, []);

    const loopTypeOptions = useMemo(
      () => [
        { value: LoopType.PROMPT, label: "Prompt Template" },
        { value: LoopType.WORKFLOW, label: "Workflow" },
      ],
      [],
    );

    const iteratorSources = useMemo(
      () =>
        Object.entries(sources).reduce(
          (acc, [key, values]) => {
            const filteredValues = values.filter(
              v => v !== "loop_index" && v !== "previous_outputs" && v !== "_iterator_item",
            );
            if (filteredValues.length > 0) {
              acc[key as SourceType] = filteredValues;
            }
            return acc;
          },
          {} as Record<SourceType, string[]>,
        ),
      [sources],
    );

    return (
      <div className="space-y-4">
        <SectionGroup
          title="Loop Type"
          description="Choose what to loop over"
          icon={<LoopIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Loop Type</label>
              <Select value={loopType} onValueChange={value => onLoopTypeChange(value as LoopType)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select loop type" />
                </SelectTrigger>
                <SelectContent>
                  {loopTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <LoopConfig
              loopType={loopType}
              promptConfig={promptConfig}
              onPromptConfigChange={onPromptConfigChange}
              workflowConfig={workflowConfig}
              onWorkflowConfigChange={onWorkflowConfigChange}
              readonly={readonly}
              showVerboseMode={showVerboseMode}
            />
          </div>
        </SectionGroup>
        <SectionGroup
          title="Iterator Configuration"
          description="Configure the collection to iterate over"
          variant="default"
          icon={<IterationCwIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            {useIteratorSource ? (
              <>
                {useJsonPath ? (
                  <ValidatedField
                    name="JSON Path"
                    path="configuration.iterator_source"
                    helpTooltip="JSON path to the array/collection to iterate over. Can use JSON path expressions like $.items or $.data.list."
                  >
                    <Input
                      placeholder="e.g., $.items, $.data.list, items"
                      value={iteratorSource || ""}
                      onChange={e => onIteratorSourceChange(e.target.value)}
                      disabled={readonly}
                    />
                  </ValidatedField>
                ) : (
                  <ValidatedField
                    name="Iterator Source"
                    path="configuration.iterator_source"
                    helpTooltip="Select an input variable or previous node output to iterate over."
                  >
                    <SelectSource
                      value={iteratorSource || undefined}
                      sources={iteratorSources}
                      onValueChange={value =>
                        (onSourceSelectorChange || onIteratorSourceChange)(value)
                      }
                      placeholder="Select an input variable or previous output..."
                      disabled={readonly}
                      type={SourceType.Column}
                      className="w-full"
                    />
                  </ValidatedField>
                )}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="use-json-path"
                    checked={useJsonPath}
                    onChange={e => onUseJsonPathChange?.(e.target.checked)}
                    disabled={readonly || isReadonly}
                    label="Use JSON Path"
                  />
                </div>
              </>
            ) : (
              <ValidatedField
                name="Max Iterations"
                path="configuration.max_iterations"
                helpTooltip="Maximum number of iterations to perform."
              >
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 10"
                  value={maxIterations?.toString() || ""}
                  onChange={e => {
                    const { value } = e.target;
                    onMaxIterationsChange?.(value ? parseInt(value, 10) : undefined);
                  }}
                  disabled={readonly}
                />
              </ValidatedField>
            )}
            <SectionGroup
              title="Advanced Settings"
              variant="compact"
              collapsible
              defaultExpanded={false}
              icon={<Settings className="h-4 w-4" />}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Use Iterator Source</span>
                  <Toggle
                    enabled={useIteratorSource}
                    setEnabled={(enabled: boolean) => onUseIteratorSourceChange?.(enabled)}
                    disabled={readonly || isReadonly}
                  />
                </div>
              </div>
            </SectionGroup>
          </div>
        </SectionGroup>
        {(inputVariables.length > 0 || inputVariablesIsLoading) && (
          <SectionGroup
            title="Input Variables"
            description="Map loop variables to data sources"
            variant="default"
            icon={<IterationCwIcon className="h-4 w-4" />}
          >
            <InputVariablesField
              variables={inputVariables}
              sources={sources}
              sourceKeyPrefix="variable_mappings"
              isLoading={inputVariablesIsLoading}
              showAutofill
              onAutofill={onAutofill}
            />
            <div className="mt-2 text-xs text-gray-500">
              Special loop variables available:
              <ul className="mt-1 list-inside list-disc">
                <li>
                  <code>loop_index</code> - Current iteration index (starts at 0)
                </li>
                <li>
                  <code>previous_outputs</code> - Array of outputs from all previous iterations
                </li>
                <li>
                  <code>_iterator_item</code> - Current item from the iterator
                </li>
              </ul>
            </div>
          </SectionGroup>
        )}
      </div>
    );
  },
);

ConfigurationView.displayName = "ForLoopConfigurationView";

export default ConfigurationView;
